import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import api from '../services/api';
import useWebSocket from '../hooks/useWebSocket';

interface ArtistProfile {
  id: string;
  artistName: string;
  bio?: string;
  socialLinks?: Record<string, string>;
  profileImageUrl?: string;
  verified: boolean;
  totalRecordings: number;
  venuesCount: number;
  totalViews: number;
  venues?: Array<{
    id: string;
    name: string;
    address: string;
    city: string;
  }>;
}

interface Recording {
  id: string;
  venueId?: string;
  venue_id?: string;
  venueName?: string;
  venue_name?: string;
  artistId?: string;
  artist_id?: string;
  artistName?: string;
  artist_name?: string;
  title: string;
  description?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  durationSeconds?: number;
  duration_seconds?: number;
  fileSizeBytes?: number;
  file_size_bytes?: number;
  status: 'pending' | 'queued' | 'recording' | 'uploading' | 'ready' | 'failed' | 'deleted' | 'processing';
  isPublic?: boolean;
  is_public?: boolean;
  viewCount?: number;
  view_count?: number;
  downloadCount?: number;
  download_count?: number;
  streamingUrl?: string;
  downloadUrl?: string;
  tags?: string[];
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  waveformData?: number[];
  waveform_data?: number[];
  // Active recording properties
  capturedMinutes?: number;
  captured_minutes?: number;
  maxDuration?: number;
  max_duration?: number;
  bufferedMinutes?: number;
  buffered_minutes?: number;
}

interface ShareLink {
  id: string;
  shareToken: string;
  shareUrl: string;
  passwordHash?: string;
  expiresAt?: string;
  maxViews?: number;
  currentViews: number;
  allowedDownloads: boolean;
  isActive: boolean;
}

interface RecordingContextType {
  profile: ArtistProfile | null;
  recordings: Recording[];
  selectedRecording: Recording | null;
  isLoading: boolean;
  error: string | null;
  
  // Profile actions
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<ArtistProfile>) => Promise<void>;
  
  // Recording actions
  fetchRecordings: (filters?: any) => Promise<void>;
  selectRecording: (id: string) => Promise<void>;
  updateRecording: (id: string, updates: Partial<Recording>) => Promise<void>;
  deleteRecording: (id: string) => Promise<void>;
  
  // Sharing actions
  createShareLink: (recordingId: string, options: {
    password?: string;
    expiresIn?: number;
    maxViews?: number;
    allowDownloads?: boolean;
  }) => Promise<ShareLink>;
  
  // Venue access
  requestVenueAccess: (accessCode: string) => Promise<void>;
}

const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

export const useRecordings = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecordings must be used within RecordingProvider');
  }
  return context;
};

interface RecordingProviderProps {
  children: ReactNode;
}

export const RecordingProvider: React.FC<RecordingProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<ArtistProfile | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // WebSocket connection for real-time updates
  const { registerHandler, subscribeToVenue } = useWebSocket();

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/artists/profile');
      setProfile(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (updates: Partial<ArtistProfile>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.put('/artists/profile', updates);
      setProfile(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRecordings = useCallback(async (filters?: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/recordings', { params: filters });
      // Ensure we always set an array
      const recordingsData = Array.isArray(response.data) ? response.data : 
                            response.data?.recordings ? response.data.recordings : [];
      setRecordings(recordingsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch recordings');
      setRecordings([]); // Reset to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectRecording = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/recordings/${id}`);
      setSelectedRecording(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch recording');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateRecording = useCallback(async (id: string, updates: Partial<Recording>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.put(`/recordings/${id}`, updates);
      
      // Update in list
      setRecordings(prev => prev.map(r => r.id === id ? response.data : r));
      
      // Update selected if it's the same
      if (selectedRecording?.id === id) {
        setSelectedRecording(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update recording');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedRecording]);

  const deleteRecording = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.delete(`/recordings/${id}`);
      
      // Remove from list
      setRecordings(prev => prev.filter(r => r.id !== id));
      
      // Clear selected if it was deleted
      if (selectedRecording?.id === id) {
        setSelectedRecording(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete recording');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedRecording]);

  const createShareLink = useCallback(async (recordingId: string, options: {
    password?: string;
    expiresIn?: number;
    maxViews?: number;
    allowDownloads?: boolean;
  }): Promise<ShareLink> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post(`/recordings/${recordingId}/share`, options);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create share link');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Register WebSocket handlers for real-time updates
  useEffect(() => {
    const cleanup = registerHandler('recording_status', (message) => {
      // Update recording status in real-time
      setRecordings(prev => prev.map(recording => {
        if (recording.id === message.recordingId) {
          return {
            ...recording,
            status: message.status,
            ...(message.s3_key && { s3_key: message.s3_key }),
          };
        }
        return recording;
      }));
      
      // Update selected recording if it matches
      setSelectedRecording(prev => {
        if (prev?.id === message.recordingId) {
          return {
            ...prev,
            status: message.status,
            ...(message.s3_key && { s3_key: message.s3_key }),
          };
        }
        return prev;
      });
    });
    
    return cleanup;
  }, [registerHandler]);
  
  // Subscribe to venues when profile is loaded
  useEffect(() => {
    if (profile?.venues) {
      profile.venues.forEach(venue => {
        subscribeToVenue(venue.id);
      });
    }
  }, [profile, subscribeToVenue]);

  const requestVenueAccess = useCallback(async (accessCode: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.post('/artists/request-venue-access', { accessCode });
      // Refresh profile to get updated venue list
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to request venue access');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile]);

  const value: RecordingContextType = {
    profile,
    recordings,
    selectedRecording,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    fetchRecordings,
    selectRecording,
    updateRecording,
    deleteRecording,
    createShareLink,
    requestVenueAccess,
  };

  return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>;
};